import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    MenuItem,
    Snackbar,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskSavedFilterService, { viewLink } from '../../services/TaskSavedFilterService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import { formatShortDate, timeAgo } from '../../utils/dates';
import { shortName } from './taskUi';

const MAX_NAME = 60;

/**
 * Names a saved filter: Save filter, Save as new, or Rename. PMs and admins can share a new one
 * with the team straight away.
 */
export const SaveFilterDialog = ({ open, title, submitLabel, initialName = '', canShare, onSubmit, onClose }) => {
    useTrackUsage('SaveFilterDialog', 'dialog', 'tasks', open);
    const [name, setName] = useState(initialName);
    const [shared, setShared] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open) {
            setName(initialName);
            setShared(false);
            setError(null);
        }
    }, [open, initialName]);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await onSubmit(name.trim(), shared);
        } catch (err) {
            setError(taskErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <form onSubmit={submit}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        inputProps={{ maxLength: MAX_NAME }}
                        helperText={`${name.length}/${MAX_NAME}`}
                    />
                    {canShare && (
                        <FormControlLabel
                            control={<Switch checked={shared} onChange={(e) => setShared(e.target.checked)} />}
                            label="Share with team"
                        />
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={busy || !name.trim()}>
                        {submitLabel}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

/** Asks before changing a filter that people outside are watching through a link. */
export const LiveLinkConfirm = ({ filter, action, onConfirm, onClose }) => (
    <Dialog open={!!filter} onClose={onClose} maxWidth="xs">
        <DialogTitle>{action} this filter?</DialogTitle>
        <DialogContent>
            <DialogContentText>
                This filter has {filter?.liveLinks} live link{filter?.liveLinks === 1 ? '' : 's'}. People with the link
                will see the change.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={onConfirm}>
                {action}
            </Button>
        </DialogActions>
    </Dialog>
);

const EXPIRY = [
    { value: 'never', label: 'Never' },
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' },
    { value: 'date', label: 'On a date' },
];

const describe = (l) => {
    const parts = [];
    if (l.expired) parts.push(`expired ${formatShortDate(l.expiresAt)}`);
    else parts.push(l.expiresAt ? `expires ${formatShortDate(l.expiresAt)}` : 'never expires');
    parts.push(`created by ${shortName(l.createdBy)}`);
    parts.push(
        l.accessCount > 0
            ? `opened ${l.accessCount} time${l.accessCount === 1 ? '' : 's'}, last ${timeAgo(l.lastAccessedAt)}`
            : 'not opened yet',
    );
    return parts.join(' · ');
};

/**
 * No-login links to a saved filter on All tasks (D35, D46): Share link makes one, and Links lists
 * them with Turn off, as on the shared folder page.
 */
export const ViewLinksDialog = ({ open, filter, onClose, onChanged }) => {
    useTrackUsage('ViewLinksDialog', 'dialog', 'tasks', open);
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expiry, setExpiry] = useState('never');
    const [date, setDate] = useState('');
    const [error, setError] = useState(null);
    const [snack, setSnack] = useState(null);
    const [confirmOff, setConfirmOff] = useState(null);
    // When the clipboard isn't allowed, the link shows in a box to copy by hand
    const [manual, setManual] = useState(null);

    const load = useCallback(async () => {
        if (!filter) return;
        setLoading(true);
        try {
            setLinks(await TaskSavedFilterService.links(filter.id));
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        if (open) {
            setError(null);
            setManual(null);
            setExpiry('never');
            setDate('');
            load();
        }
    }, [open, load]);

    const expiresAt = () => {
        if (expiry === 'never') return null;
        if (expiry === 'date') return date ? new Date(`${date}T23:59:59`).toISOString() : null;
        return new Date(Date.now() + Number(expiry) * 86400000).toISOString();
    };

    const copy = async (token) => {
        try {
            await navigator.clipboard.writeText(viewLink(token));
            setSnack('Link copied');
            setManual(null);
        } catch {
            setManual(viewLink(token));
        }
    };

    const create = async () => {
        setError(null);
        try {
            const link = await TaskSavedFilterService.createLink(filter.id, expiresAt());
            await load();
            onChanged?.();
            await copy(link.token);
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    const turnOff = async () => {
        const link = confirmOff;
        setConfirmOff(null);
        try {
            await TaskSavedFilterService.turnOffLink(filter.id, link.id);
            setSnack('Link turned off');
            await load();
            onChanged?.();
        } catch (e) {
            setError(taskErrorMessage(e));
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Share {filter?.name}</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2">Create a link</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <TextField
                            select
                            size="small"
                            label="Expires"
                            value={expiry}
                            onChange={(e) => setExpiry(e.target.value)}
                            sx={{ minWidth: 130 }}
                        >
                            {EXPIRY.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {expiry === 'date' && (
                            <TextField
                                size="small"
                                type="date"
                                label="Expiry date"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Anyone with this link sees this filter's calendar or list, read only, without logging in. Each
                        task shows only its title, language, stage, posting date and category: no people, emails,
                        comments or due dates.
                    </Typography>
                    <Button variant="contained" sx={{ mt: 1.5 }} onClick={create} disabled={expiry === 'date' && !date}>
                        Create and copy link
                    </Button>
                    {manual && (
                        <TextField
                            fullWidth
                            size="small"
                            sx={{ mt: 1.5 }}
                            label="Copy this link"
                            value={manual}
                            autoFocus
                            onFocus={(e) => e.target.select()}
                            InputProps={{ readOnly: true }}
                        />
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2">Links</Typography>
                    {loading && <CircularProgress size={20} sx={{ mt: 1 }} />}
                    {!loading && links.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No links yet.
                        </Typography>
                    )}
                    {links.map((l) => (
                        <Box
                            key={l.id}
                            sx={{
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                opacity: l.expired ? 0.55 : 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                noWrap
                                title={viewLink(l.token)}
                                sx={{ fontFamily: 'monospace' }}
                            >
                                {viewLink(l.token)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div">
                                {describe(l)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                {!l.expired && (
                                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copy(l.token)}>
                                        Copy
                                    </Button>
                                )}
                                <Button size="small" color="error" onClick={() => setConfirmOff(l)}>
                                    Turn off
                                </Button>
                            </Box>
                        </Box>
                    ))}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Done</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!confirmOff} onClose={() => setConfirmOff(null)} maxWidth="xs">
                <DialogTitle>Turn off this link?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Anyone who opens it will see "This link is no longer available". This can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOff(null)}>Cancel</Button>
                    <Button color="error" onClick={turnOff}>
                        Turn off
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snack}
                autoHideDuration={4000}
                onClose={() => setSnack(null)}
                message={<span aria-live="polite">{snack}</span>}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            />
        </>
    );
};
